import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Car, Users, Award, Shield, Star, Clock, MapPin, Headphones, CheckCircle, TrendingUp, ThumbsUp, Globe } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const About = () => {
  const [aboutData, setAboutData] = useState({
    about_title: 'About HYPA MOTORS',
    about_subtitle: 'Your Trusted Partner for Luxury Travel in Rwanda',
    about_description: '',
    about_mission: '',
    about_vision: '',
    about_image: '',
    about_why_choose_us: [],
    about_values: [],
    about_stats: [],
    about_page_enabled: 'true'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAboutData();
    window.scrollTo(0, 0);
  }, []);

  const fetchAboutData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings/public`);
      
      // Parse JSON fields
      let whyChooseUs = [];
      let values = [];
      let stats = [];
      
      try {
        whyChooseUs = response.data.about_why_choose_us ? 
          (typeof response.data.about_why_choose_us === 'string' ? 
            JSON.parse(response.data.about_why_choose_us) : response.data.about_why_choose_us) : [];
      } catch (e) { whyChooseUs = []; }
      
      try {
        values = response.data.about_values ? 
          (typeof response.data.about_values === 'string' ? 
            JSON.parse(response.data.about_values) : response.data.about_values) : [];
      } catch (e) { values = []; }
      
      try {
        stats = response.data.about_stats ? 
          (typeof response.data.about_stats === 'string' ? 
            JSON.parse(response.data.about_stats) : response.data.about_stats) : [];
      } catch (e) { stats = []; }
      
      setAboutData({
        about_title: response.data.about_title || 'About HYPA MOTORS',
        about_subtitle: response.data.about_subtitle || 'Your Trusted Partner for Luxury Travel in Rwanda',
        about_description: response.data.about_description || '',
        about_mission: response.data.about_mission || '',
        about_vision: response.data.about_vision || '',
        about_image: response.data.about_image || '',
        about_why_choose_us: whyChooseUs,
        about_values: values,
        about_stats: stats,
        about_page_enabled: response.data.about_page_enabled || 'true'
      });
    } catch (error) {
      console.error('Error fetching about data:', error);
    } finally {
      setLoading(false);
    }
  };

  const defaultWhyChooseUs = [
    { icon: 'Car', title: 'Premium Fleet', description: 'Access to the most exclusive luxury vehicles in Rwanda' },
    { icon: 'Shield', title: 'Fully Insured', description: 'Complete peace of mind with comprehensive insurance' },
    { icon: 'Headphones', title: '24/7 Support', description: 'Round-the-clock customer assistance' },
    { icon: 'Award', title: 'Award Winning', description: 'Recognized for excellence in service' }
  ];

  const defaultStats = [
    { number: '500+', label: 'Happy Customers' },
    { number: '50+', label: 'Luxury Vehicles' },
    { number: '10+', label: 'Tour Packages' },
    { number: '4.9', label: 'Customer Rating' }
  ];

  const getIconComponent = (iconName) => {
    const icons = {
      Car: Car,
      Shield: Shield,
      Headphones: Headphones,
      Award: Award,
      Users: Users,
      Star: Star,
      Clock: Clock,
      MapPin: MapPin,
      CheckCircle: CheckCircle,
      TrendingUp: TrendingUp,
      ThumbsUp: ThumbsUp,
      Globe: Globe
    };
    return icons[iconName] || CheckCircle;
  };

  const whyChooseUsItems = aboutData.about_why_choose_us.length > 0 ? aboutData.about_why_choose_us : defaultWhyChooseUs;
  const statsItems = aboutData.about_stats.length > 0 ? aboutData.about_stats : defaultStats;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-dark-800">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (aboutData.about_page_enabled !== 'true') {
    return null;
  }

  return (
    <div className="bg-dark-800 min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[40vh] md:h-[50vh] bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: aboutData.about_image 
            ? `url(${aboutData.about_image})`
            : 'url(https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?ixlib=rb-4.0.3)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent">
          <div className="container mx-auto px-4 h-full flex items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                {aboutData.about_title}
              </h1>
              <p className="text-lg md:text-xl text-gray-300">
                {aboutData.about_subtitle}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 bg-dark-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <p className="text-gray-300 text-lg leading-relaxed">
              {aboutData.about_description || 'HYPA MOTORS is Rwanda\'s premier car rental and tourism company, dedicated to providing exceptional luxury travel experiences. With years of experience in the industry, we have built a reputation for excellence, reliability, and customer satisfaction.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-16 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="glass-card rounded-xl p-8 text-center"
            >
              <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp size={32} className="text-orange-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
              <p className="text-gray-400 leading-relaxed">
                {aboutData.about_mission || 'To provide unparalleled luxury transportation and tour experiences that exceed our clients\' expectations, while showcasing the beauty and culture of Rwanda.'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="glass-card rounded-xl p-8 text-center"
            >
              <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe size={32} className="text-orange-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Our Vision</h2>
              <p className="text-gray-400 leading-relaxed">
                {aboutData.about_vision || 'To become the most trusted and recognized luxury car rental and tourism brand in East Africa, setting the standard for excellence in customer service and vehicle quality.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-dark-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Choose HYPA MOTORS</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Experience the difference with our premium services
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUsItems.map((item, index) => {
              const IconComponent = getIconComponent(item.icon);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card rounded-xl p-6 text-center hover:shadow-elegant transition"
                >
                  <div className="w-14 h-14 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent size={28} className="text-orange-500" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {statsItems.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-orange-500 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-red-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready for Your Luxury Experience?
          </h2>
          <p className="text-white text-lg mb-8 max-w-2xl mx-auto">
            Contact us today to book your dream car or tour package in Rwanda
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link 
              to="/cars" 
              className="bg-white text-orange-500 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
            >
              Browse Vehicles
            </Link>
            <Link 
              to="/contact" 
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-orange-500 transition"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;